import { useMount, useUnmount } from 'ahooks';
import { Divider } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ReactComponent as ProductImg } from 'assets/img/products.svg';
import { RevxSpin } from 'components/RevxSpin';
import { BUSINESS_TYPE_ENUM } from 'domains/gallerySkuDomain';
import { GallerySku } from 'domains/gallerySkuDomain';
import { useCanvaMatchView } from 'hooks';
import { useCanvaPublishDraft } from 'hooks/useCanvaPublishDraft';
import { useOrgId } from 'hooks/useOrg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import './GalleryLayout.less';
import { BusinessTypeGallerySlick } from './components/BusinessTypeGallerySlick';
import {
  CanvaMatchView,
  CompanyCard,
} from 'features/CompanyGallery/components';
import { GalleryCard } from './components/GalleryCard';
import { GallerySlick } from './components/GallerySlick';

const GalleryLayout = () => {
  const { t } = useTranslation();
  const params = useParams<{ businessType: BUSINESS_TYPE_ENUM }>();
  const dispatch = useAppDispatch();
  const startingJobConfig = useAppSelector(
    (state) => state.gallery.startingJobConfig
  );
  const orgId = useOrgId();
  const businessType = params.businessType;
  const { isMatchView, categoryId, isTaggedPick, browseAllProducts } =
    useCanvaMatchView();
  const { publishWithSku, publishing } = useCanvaPublishDraft();
  const galleryLabels = useAppSelector((state) => state.gallery.galleryLabels);
  // Named after the category the buyer opened, so the picker does not read as a second copy of
  // the landing view. Empty until the labels land, which leaves the circle unlabelled briefly.
  const categoryName = useMemo(
    () =>
      galleryLabels.find((item) => item.skuLabel.id === categoryId)?.skuLabel
        .name,
    [galleryLabels, categoryId]
  );
  // How many products fit the design, for the heading. Undefined until the list settles.
  const [matchCount, setMatchCount] = useState<number>();
  const handleMatchCount = useCallback(
    (total: number) => {
      setMatchCount(total);
      // Nothing fits: send the buyer straight to the full gallery rather than showing an empty
      // grid and asking them to find the way out. Replaces the history entry so Back does not
      // return to the empty view and bounce them forward again.
      if (total === 0) {
        browseAllProducts({ replace: true });
      }
    },
    [browseAllProducts]
  );
  // The picker's own count. It shows whatever the category holds, so zero is not a reason to
  // move the buyer on the way an empty match list is.
  const [categoryCount, setCategoryCount] = useState<number>();
  const handleCategoryLoaded = useCallback(
    (total: number, skus: GallerySku[]) => {
      setCategoryCount(total);
      // One product in the category is not a choice. The buyer reached this route from
      // somewhere that could not know the count -- the header's Shop menu, say -- so the pick
      // is made for them rather than shown as a page with a single card on it.
      if (total === 1 && skus[0]) {
        publishWithSku(Number(skus[0].id));
      }
    },
    [publishWithSku]
  );

  useMount(() => {
    // dispatch.gallery.getGalleryLabels();
    dispatch.org.getInternalDistributionAddresses(
      Number(localStorage.getItem('org') || orgId)
    );
  });

  useEffect(() => {
    if (businessType) {
      dispatch.gallery.setState({
        selectedBusinessType: businessType,
      });
      dispatch.gallery.getPreConfiguredSkus(businessType);
    } else {
      dispatch.gallery.setState({
        selectedBusinessType: null,
        preConfiguredSkus: [],
      });
    }
  }, [businessType, dispatch.gallery]);

  useUnmount(() => {
    dispatch.gallery.setState({
      selectedBusinessType: null,
    });
  });

  // The hand-off named the products: the design's brand template resolved to more than one
  // SKU, and the buyer is choosing between exactly those. No category strip, no size matching,
  // no way out to the rest of the catalogue -- Canva already decided what is on offer.
  if (isTaggedPick) {
    return (
      <div className="gallery-page">
        <RevxSpin spinning={startingJobConfig}>
          <div className="gallery">
            <div className="company-gallery-sku-container">
              <CompanyCard key="canva-tagged" />
            </div>
          </div>
        </RevxSpin>
      </div>
    );
  }

  // A category opened out of the full gallery, when it holds more than one product: the publish
  // still needs to know which of them the design goes on, and picking one finishes it the same
  // way picking a match does. Checked first -- a category is its own view, not the matches.
  if (categoryId) {
    return (
      <div className="gallery-page">
        <RevxSpin spinning={startingJobConfig || publishing}>
          <div className="gallery">
            <CanvaMatchView
              count={categoryCount}
              title={categoryName || t('gallery.canvaMatch.chooseProduct')}
            >
              <div className="company-gallery-sku-container">
                <CompanyCard
                  key={`canva-category-${categoryId}`}
                  galleryLabelId={categoryId}
                  onTotalChange={handleCategoryLoaded}
                />
              </div>
            </CanvaMatchView>
          </div>
        </RevxSpin>
      </div>
    );
  }

  // The Canva landing: the products that can hold the design, gathered across categories, since
  // the ones that fit are spread over them and the category strip would hide most.
  //
  // Only this state. "Browse all products" falls through to the ordinary category page below --
  // the buyer who has rejected the recommendations wants to shop the catalogue the normal way,
  // not scroll a flat list of everything.
  if (isMatchView) {
    return (
      <div className="gallery-page">
        <RevxSpin spinning={startingJobConfig}>
          <div className="gallery">
            <CanvaMatchView count={matchCount}>
              {/* The company gallery's container, not this page's: the grid inside CompanyCard is
                  written against it, and the category-oriented .gallery-container lays the cards
                  out flush left instead. */}
              <div className="company-gallery-sku-container">
                <CompanyCard key="canva-match" onTotalChange={handleMatchCount} />
              </div>
            </CanvaMatchView>
          </div>
        </RevxSpin>
      </div>
    );
  }

  return (
    <div>
      <RevxSpin spinning={startingJobConfig}>
        <div className="gallery">
          {!businessType && (
            <div className="gallery-title">
              <ProductImg style={{ width: 161 }} />
            </div>
          )}
          <div className="gallery-slick-container">
            {businessType ? (
              <BusinessTypeGallerySlick
                businessType={businessType}
                key={businessType}
              />
            ) : (
              <GallerySlick />
            )}
          </div>
          {/* <RevxFlexLayout
            justify="justify-end"
            className="gallery-slick-render"
          >
            <RevxButton
              type="link"
              onClick={() => {
                dispatch.gallery.setGalleryRenderType(
                  galleryRenderType === GALLERY_RENDER_ENUM.DEFAULT
                    ? GALLERY_RENDER_ENUM.LIST
                    : GALLERY_RENDER_ENUM.DEFAULT
                );
              }}
            >
              {galleryRenderType === GALLERY_RENDER_ENUM.DEFAULT ? (
                <GalleryCardIcon />
              ) : (
                <GalleryListIcon />
              )}
            </RevxButton>
          </RevxFlexLayout> */}
          <Divider className="gallery-divider" />
          <div className="gallery-container">
            <GalleryCard key={'card'} businessType={businessType} />
          </div>
        </div>
      </RevxSpin>
    </div>
  );
};

export default GalleryLayout;
